import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isTransactable, listAccounts } from '@/api/accounts'
import {
  recordPersonalLoanPayment,
  type PersonalLoan,
  type RecordPaymentPayload,
} from '@/api/personalLoans'
import { DatePicker } from '@/components/DatePicker'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { fromPaise, toPaise } from '@/lib/splitMath'
import { today } from '@/lib/transactionDateRangeFilter'
import { MODAL_BOX, MODAL_OVERLAY } from '@/components/transactions/TransactionForms'
import {
  LABEL_CLS,
  errorDetail,
  invalidateAfterLoanWrite,
  parsePaise,
  warnIfEarmarkShort,
} from './shared'

export function RecordPaymentModal({ loan, onClose }: { loan: PersonalLoan; onClose: () => void }) {
  const { format } = useFormatCurrency()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [principalText, setPrincipalText] = useState('')
  const [interestText, setInterestText] = useState('')
  const [accountId, setAccountId] = useState('')
  const [paidOn, setPaidOn] = useState(today())
  const [error, setError] = useState<string | null>(null)

  const { data: accountsData } = useQuery({ queryKey: ['accounts'], queryFn: () => listAccounts() })
  const accounts = (accountsData?.accounts ?? []).filter(
    (a) => isTransactable(a.type) && a.id !== loan.accountId
  )
  const lent = loan.direction === 'LENT'

  const mutation = useMutation({
    mutationFn: (payload: RecordPaymentPayload) =>
      recordPersonalLoanPayment(loan.accountId, payload),
    onSuccess: (data) => {
      invalidateAfterLoanWrite(queryClient)
      warnIfEarmarkShort(data?.earmarkWarning, format, navigate)
      onClose()
    },
    onError: (err) => setError(errorDetail(err, 'Something went wrong. Please try again.')),
  })

  function submit() {
    const principal = principalText.trim() ? parsePaise(principalText) : 0
    const interest = interestText.trim() ? parsePaise(interestText) : 0
    if (principal === null || interest === null || principal < 0 || interest < 0) {
      setError('Amounts must be numbers of ₹0 or more')
      return
    }
    if (principal === 0 && interest === 0) {
      setError('Enter a principal or interest amount')
      return
    }
    if (principal > toPaise(loan.outstanding)) {
      setError(
        `Principal can't be more than the ${format(loan.outstanding)} outstanding — record the extra as interest`
      )
      return
    }
    setError(null)
    mutation.mutate({
      paidOn,
      financialAccountId: accountId,
      principal: fromPaise(principal),
      interest: fromPaise(interest),
    })
  }

  return (
    <div className={MODAL_OVERLAY}>
      <div className={MODAL_BOX} role="dialog" aria-label="Record payment">
        <h2 className="mb-1 text-lg font-bold text-[var(--fl-text-1)]">Record payment</h2>
        <p className="mb-4 text-sm text-[var(--fl-text-2)]">
          {format(loan.outstanding)} outstanding
        </p>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="payment-principal" className={LABEL_CLS}>
                Principal
              </label>
              <Input
                id="payment-principal"
                inputMode="decimal"
                placeholder="0"
                value={principalText}
                onChange={(e) => setPrincipalText(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="payment-interest" className={LABEL_CLS}>
                Interest
              </label>
              <Input
                id="payment-interest"
                inputMode="decimal"
                placeholder="0"
                value={interestText}
                onChange={(e) => setInterestText(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className={LABEL_CLS}>{lent ? 'Received into' : 'Paid from'}</label>
            <Select aria-label="Account" value={accountId} onChange={setAccountId}>
              <option value="">Select account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label htmlFor="payment-date" className={LABEL_CLS}>
              Date
            </label>
            <DatePicker id="payment-date" value={paidOn} onChange={setPaidOn} />
          </div>
          {error && <p className="text-xs text-[var(--fl-error)]">{error}</p>}
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={accountId === '' || mutation.isPending}
              className="flex-1 font-semibold"
            >
              {mutation.isPending ? 'Saving…' : 'Record payment'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
